import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, CheckCircle2, Plus } from "lucide-react";
import { Panel, Empty } from "../components/ui";
import { formatDate } from "../utils/calendar.mjs";
import { needStatuses } from "../services/commercial.mjs";
export default function Needs({
  state,
  focusId,
  onNew,
  onCreateQuote,
  onSend,
  onSign,
  onRefuse,
  onStatus,
  onPayload,
}) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const needs = state.needs.filter(
    (n) =>
      (!focusId || n.id === focusId) &&
      (filter === "all" || n.status === filter),
  );
  return (
    <>
      <Panel
        title="Besoins commerciaux"
        action={
          <button className="secondary" onClick={onNew}>
            <Plus />
            Nouveau besoin
          </button>
        }
      >
        <div className="taskFilters">
          <button
            aria-pressed={filter === "all"}
            className={filter === "all" ? "active" : ""}
            onClick={() => setFilter("all")}
          >
            Tous ({state.needs.length})
          </button>
          {needStatuses.map((status) => (
            <button
              key={status}
              aria-pressed={filter === status}
              className={filter === status ? "active" : ""}
              onClick={() => setFilter(status)}
            >
              {status} ({state.needs.filter((n) => n.status === status).length})
            </button>
          ))}
        </div>
        <p className="hint">
          Créez un devis complet avec prévisualisation PDF. Les devis enregistrés sont disponibles dans Devis et dans l’admin.
        </p>
      </Panel>
      {needs.map((need) => {
        const client = state.clients.find((c) => c.id === need.clientId),
          contact =
            client?.contacts.find((c) => c.id === need.contactId) ||
            client?.contacts.find((c) => c.active && c.primary),
          quotes = state.quotes
            .filter((q) => q.needId === need.id)
            .sort((a, b) => b.version - a.version);
        return (
          <Panel key={need.id} title={client?.name || need.client}>
            <div className="needSummary">
              <div>
                <h3>
                  {need.job} × {need.qty}
                </h3>
                <span className="status">{need.status}</span>
                <p className="muted">
                  {need.type} · Démarrage : {formatDate(need.start)} · Contact :{" "}
                  {contact?.name || "À rechercher"}
                  <br />
                  Durée : {need.duration || "À compléter"} · Horaires :{" "}
                  {need.hours || "À compléter"}
                  <br />
                  Urgence : {need.urgency || "Non renseignée"} · Contraintes :{" "}
                  {need.constraints || "À compléter"}
                </p>
                {need.notes && <p className="muted">{need.notes}</p>}
              </div>
              {need.status !== "signé" && (
                <div className="needActions">
                  <button
                    className="primary"
                    onClick={() => onCreateQuote(need)}
                  >
                    <FileText />
                    {quotes.length
                      ? "Créer une nouvelle version"
                      : "Créer le devis"}
                  </button>
                  <select
                    aria-label={`Statut du besoin ${need.job}`}
                    value={need.status}
                    onChange={(e) => onStatus(need.id, e.target.value)}
                  >
                    {needStatuses.map((status) => (
                      <option
                        key={status}
                        disabled={["signé", "devis envoyé"].includes(status)}
                      >
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {quotes.map((q) => (
              <div className="quoteVersion" key={q.id}>
                <div>
                  <b>Devis {q.quotationNumber ? `n° ${q.quotationNumber}` : `V${q.version}`}</b>{" "}
                  <span className="status">{q.status}</span>
                  <p className="muted">
                    Coefficient : {q.coefficient ?? "—"} · Taux :{" "}
                    {q.rate ?? "—"} € · Validité : {formatDate(q.validUntil)}
                    <br />
                    Conditions : {q.conditions}
                  </p>
                  {q.refusalReason && (
                    <p className="error">Refus : {q.refusalReason}</p>
                  )}
                </div>
                <div className="needActions">
                  <button
                    className="secondary"
                    onClick={() => q.apiQuotationId ? navigate(`/devis?quote=${encodeURIComponent(q.apiQuotationId)}`) : onCreateQuote(need)}
                  >
                    {q.apiQuotationId ? "Prévisualiser / gérer le devis" : "Recréer au format devis admin"}
                  </button>
                  {!q.apiQuotationId && need.status !== "signé" &&
                    ["créé", "négociation"].includes(q.status) && (
                      <button className="primary" onClick={() => onSend(q.id)}>
                        Déclarer le devis envoyé
                      </button>
                    )}
                  {!q.apiQuotationId && need.status !== "signé" &&
                    ["envoyé", "négociation"].includes(q.status) && (
                      <button className="primary" onClick={() => onSign(q.id)}>
                        <CheckCircle2 />
                        Enregistrer la signature
                      </button>
                    )}
                  {!q.apiQuotationId && need.status !== "signé" && q.status !== "refusé" && (
                    <button className="secondary" onClick={() => onRefuse(q)}>
                      Devis refusé
                    </button>
                  )}
                </div>
              </div>
            ))}
            {need.transmittedAt && (
              <div className="success">
                <b>🟢 DEVIS SIGNÉ · Opportunité gagnée</b>
                <p>
                  ✓ Besoin transmis automatiquement au recrutement le{" "}
                  {new Date(need.transmittedAt).toLocaleString("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  .
                </p>
                {need.missingFields?.length > 0 && (
                  <p>
                    Informations à compléter : {need.missingFields.join(", ")}
                  </p>
                )}
                <button
                  className="secondary"
                  onClick={() => onPayload(need.id)}
                >
                  Voir les informations transmises
                </button>
              </div>
            )}
          </Panel>
        );
      })}
      {!needs.length && <Empty>Aucun besoin dans cette étape.</Empty>}
      <p className="hint">
        Prototype front : les déclarations d’envoi et de signature sont
        enregistrées localement. Aucun email ni appel au système recrutement
        n’est envoyé.
      </p>
    </>
  );
}
